import { Dayjs } from "dayjs";
import { useCallback, useState } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { clearSanitizedBins } from "~/features/autostorePutaway/autostorePutaway.slice";
import { AutostorePutawayModal as AutostorePutawayModalComponent } from "~/features/autostorePutawayModal/AutostorePutawayModal";
import { getBinCompartmentOccupancy } from "~/lib/binCompartmentOccupancy";
import useWindowDimensions from "~/lib/browserDimensions";
import { getInventoryDateObj } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { binNotAtPort, closeWorkstation } from "~/redux/actions";
import { putAway } from "~/redux/actions/inventory";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectPortStateByPort } from "~/redux/selectors/autostoreSelectors";

import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { NextBinResponse, NextEmptyBinResponse } from "~/types/api";

import {
  setIsAutostorePutawayDialogOpen,
  setIsAdjustingBins,
  setSelectedCompartmentNumber,
  resetBinAtPortSeconds,
  setIsFetchingBin
} from "./inventory.slice";
import { useGridSelector } from "./useGridSelector";

type Props = {
  handleRefetchInventory: () => void;
  resetView: (resetInv: boolean) => void;
};

export function AutostorePutawayModal({
  handleRefetchInventory,
  resetView
}: Props) {
  const dispatch = useAppDispatch();
  const trackedModalName = useMixpanelModalName("Add Inventory");
  const { width } = useWindowDimensions();

  const { autostoreGridOverride } = useGridSelector();

  const { inv_inventoryDateLabel, inv_inventoryDateRequired } =
    useAppSelector(selectServerConfig);
  const isAutostorePutawayDialogOpen = useAppSelector(
    (state) => state.inventoryNew.isAutostorePutawayDialogOpen
  );
  const isAdjustingBins = useAppSelector(
    (state) => state.inventoryNew.isAdjustingBins
  );
  const isChangeBinConfigModalOpen = useAppSelector(
    (state) => state.inventoryNew.isChangeBinConfigModalOpen
  );
  const isInventoryAdjustDialogOpen = useAppSelector(
    (state) => state.inventoryNew.isInventoryAdjustDialogOpen
  );
  const portStateByPort = useAppSelector(selectPortStateByPort);
  const selectedCompartmentInfo = useAppSelector(
    (state) => state.inventoryNew.selectedCompartment
  );
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const sanitizedBins = useAppSelector(
    (state) => state.autostorePutaway.sanitizedBins
  );

  const [inventoryDate, setInventoryDate] = useState<Dayjs | null>(null);
  const [changedQuantity, setChangedQuantity] = useState<number | null>(null);
  const [lotNumber, setLotNumber] = useState<string | null>(null);

  const selectedAutostoreGridId = siteWorkstation
    ? siteWorkstation.autostoreGridId
    : autostoreGridOverride;

  /** selected compartment's bin data */
  const selectedCompartmentBin =
    selectedCompartmentInfo.bin?.autostoreBinCompartments.find(
      (binCompartment) =>
        binCompartment.autostoreCompartmentNumber ===
        (selectedCompartmentInfo.compartmentNumber || 0) + 1
    );

  const adjustingPortId = selectedCompartmentInfo.bin?.openBinResponse.portId;
  const selectedBinId = selectedCompartmentInfo.bin?.openBinResponse.binId;
  const isAdjustingPortReady =
    adjustingPortId &&
    portStateByPort[adjustingPortId]?.getPortResponse.isReady;

  const handleClose = useCallback(async () => {
    dispatch(setIsAutostorePutawayDialogOpen(false));
    await dispatch(closeWorkstation());
    handleRefetchInventory();
    dispatch(setIsAdjustingBins(false));
    setInventoryDate(null);
    setLotNumber(null);
    if (!isInventoryAdjustDialogOpen && !isAdjustingBins) {
      resetView(false);
      dispatch(resetBinAtPortSeconds());
      dispatch(binNotAtPort());
    }
  }, [
    dispatch,
    handleRefetchInventory,
    isAdjustingBins,
    isInventoryAdjustDialogOpen,
    resetView
  ]);

  return (
    selectedCompartmentInfo.bin && (
      <AutostorePutawayModalComponent
        open={isAutostorePutawayDialogOpen && !isChangeBinConfigModalOpen}
        binLoading={!isAdjustingPortReady}
        onClose={handleClose}
        showCancelButton
        binId={selectedBinId || null}
        portId={adjustingPortId || undefined}
        onPutawayButtonClick={async () => {
          if (
            !selectedVariant ||
            !selectedCompartmentInfo ||
            !selectedCompartmentBin
          )
            return;
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Inventory Adjusted"
          });
          const wasTargetBinSanitized =
            selectedBinId && sanitizedBins.includes(selectedBinId);
          await dispatch(
            putAway(
              {
                autostoreGridId: selectedAutostoreGridId,
                autostorePortId: adjustingPortId,
                binId: selectedCompartmentBin.binId,
                variantId: selectedVariant.variantId,
                count: {
                  value: changedQuantity || 1,
                  units: selectedVariant.pickingUnits
                },
                ...getInventoryDateObj(inv_inventoryDateLabel, inventoryDate),
                lotNumber: lotNumber ?? undefined,
                wasTargetBinSanitized: !!wasTargetBinSanitized
              },
              siteWorkstation?.id
            )
          );
          setInventoryDate(null);
          dispatch(setIsAutostorePutawayDialogOpen(false));
          await dispatch(closeWorkstation());
          setChangedQuantity(null);
          setLotNumber(null);
          handleRefetchInventory();
          dispatch(setIsAdjustingBins(false));
          dispatch(setIsFetchingBin(false));
          dispatch(clearSanitizedBins());
        }}
        changeQuantityFunc={setChangedQuantity}
        numberOfRows={
          (selectedCompartmentInfo.bin as NextEmptyBinResponse)
            .autostoreBinConfiguration?.horizontalCompartmentCount ||
          (selectedCompartmentInfo.bin as NextBinResponse).binConfiguration
            ?.horizontalCompartmentCount
        }
        numberOfColumns={
          (selectedCompartmentInfo.bin as NextEmptyBinResponse)
            .autostoreBinConfiguration?.verticalCompartmentCount ||
          (selectedCompartmentInfo.bin as NextBinResponse).binConfiguration
            ?.verticalCompartmentCount
        }
        selectedCompartment={
          selectedCompartmentInfo?.compartmentNumber ?? undefined
        }
        setSelectedCompartment={(compartmentNumber: number) => {
          dispatch(setSelectedCompartmentNumber(compartmentNumber));
        }}
        compartmentOccupancy={getBinCompartmentOccupancy(
          selectedCompartmentInfo
        )}
        inv_inventoryDateLabel={inv_inventoryDateLabel}
        inventoryDate={inventoryDate}
        onDateChange={setInventoryDate}
        lotNumber={lotNumber}
        onLotNumberChange={setLotNumber}
        inv_inventoryDateRequired={inv_inventoryDateRequired}
        maxWidth="lg"
        browserWidth={width}
        showProductSearch
        isExpirationRequired={selectedVariant?.isExpirationRequired ?? false}
      />
    )
  );
}
